<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="showPrompt"
        class="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6"
      >
        <button
          @click="dismiss"
          class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div class="flex items-start gap-4">
          <div class="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden">
            <!-- Use :src (runtime binding) so Vite's template compiler never
                 resolves this path through the asset/virtual-module pipeline -->
            <img
              :src="logoSrc"
              alt="Tayba Travel"
              class="w-full h-full object-cover"
            />
          </div>

          <div class="flex-1 min-w-0">
            <h3 class="text-lg font-bold font-outfit text-gray-900 mb-1">
              Installer l'application
            </h3>

            <!-- Chrome/Edge: Show install button -->
            <template v-if="!isSafari">
              <p class="text-sm text-gray-600 mb-4">
                Accédez rapidement à Tayba Travel depuis votre écran d'accueil
              </p>
              <div class="flex gap-2">
                <button
                  @click="install"
                  class="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold py-2.5 px-4 rounded-full transition-colors"
                >
                  Installer
                </button>
                <button
                  @click="dismiss"
                  class="px-4 py-2.5 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                >
                  Plus tard
                </button>
              </div>
            </template>

            <template v-else>
              <div class="text-sm text-gray-600 mb-4 space-y-2">
                <p class="font-medium">
                  Pour installer sur {{ isIOS ? "iOS" : "Safari" }} :
                </p>
                <ol class="list-decimal list-inside space-y-1 text-xs">
                  <li>
                    Appuyez sur
                    <span
                      class="inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded"
                    >
                      <svg
                        class="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"
                        />
                      </svg>
                      Partager
                    </span>
                  </li>
                  <li>Sélectionnez "Sur l'écran d'accueil"</li>
                  <li>Appuyez sur "Ajouter"</li>
                </ol>
              </div>
              <button
                @click="dismiss"
                class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                J'ai compris
              </button>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";

const showPrompt = ref(false);
const isSafari = ref(false);
const isIOS = ref(false);

// $pwa is injected by @vite-pwa/nuxt when client.installPrompt: true.
// It captures beforeinstallprompt early (before onMounted) so we never miss it.
const { $pwa } = useNuxtApp();

// Kept as a runtime string so Vite's template compiler never resolves it
// through the virtual-module / asset pipeline (which would cause a MIME error).
const logoSrc = "/pwa-192x192.png";

const isDismissedRecently = () => {
  const dismissed = localStorage.getItem("pwa-prompt-dismissed");
  if (!dismissed) return false;
  return Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000;
};

const dismiss = () => {
  showPrompt.value = false;
  if ($pwa?.cancelInstall) $pwa.cancelInstall();
  localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
};

const install = async () => {
  if ($pwa?.install) {
    await $pwa.install();
  }
  showPrompt.value = false;
};

const detectBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  isIOS.value = /iphone|ipad|ipod/.test(ua);
  isSafari.value = /safari/.test(ua) && !/chrome|chromium|crios|edg/.test(ua);
};

// Watch $pwa.showInstallPrompt — it may become true after mounting
if ($pwa) {
  watch(
    () => $pwa.showInstallPrompt,
    (ready) => {
      if (
        ready &&
        !showPrompt.value &&
        !isDismissedRecently() &&
        !window.matchMedia("(display-mode: standalone)").matches
      ) {
        setTimeout(() => {
          showPrompt.value = true;
        }, 3000);
      }
    },
  );
}

onMounted(() => {
  detectBrowser();

  // Already running as installed PWA — don't prompt again
  if (window.matchMedia("(display-mode: standalone)").matches) return;

  // In dev, always show the prompt after 1s (bypass dismiss guard so you can preview it)
  if (import.meta.dev) {
    setTimeout(() => {
      showPrompt.value = true;
    }, 1000);
    return;
  }

  // Dismissed within the last 7 days
  if (isDismissedRecently()) return;

  if (isSafari.value) {
    // Safari/iOS: no beforeinstallprompt — show manual instructions
    setTimeout(() => {
      showPrompt.value = true;
    }, 3000);
    return;
  }

  // Chrome/Edge: if $pwa already captured the event, show now
  if ($pwa?.showInstallPrompt) {
    setTimeout(() => {
      showPrompt.value = true;
    }, 3000);
  }
  // Otherwise the watcher above will trigger when the event fires

  window.addEventListener("appinstalled", () => {
    showPrompt.value = false;
  });
});
</script>
